/// <amd-module name="Wit/QueriesHub/ResultsView/ResultsTree" />
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { IObservableArray, IObservableValue, IReadyableObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ColumnDragDropBehavior, ColumnDragDropBehaviorOptions } from "VSSUI/Components/Table/ColumnDragDropBehavior";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Icon, IconSize } from "VSSUI/Icon";
import { IIdentity } from "VSSUI/IdentityPicker";
import { Intersection } from "VSSUI/Intersection";
import { Link } from "VSSUI/Link";
import { BoltListDragEvent, cellFromEvent, IListDropData, ListDragImage, ListSelection, renderListCell } from "VSSUI/List";
import { ContextMenuBehavior } from "VSSUI/Menu";
import { IObservableExpression, Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import {
    ColumnFill,
    ColumnFillId,
    ColumnSorting,
    ITableColumn,
    renderEmptyCell,
    renderLoadingCell,
    SimpleTableCell,
    SortOrder,
    TableHeaderCell
} from "VSSUI/Table";
import { ExpandableTreeCell, ITreeColumn, ITreeProps, ITreeRow, ITreeRowDetails, renderTreeRow, Tree, TreeRow } from "VSSUI/TreeEx";
import { css } from "VSSUI/Util";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { Dictionary } from "Wit/Common/Dictionary";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IQueryService, QueryDataProviders, QueryHubView } from "Wit/Common/QueryService/IQueryService";
import { convertValueToDisplayString } from "Wit/Common/QueryUtils/QueryUtils";
import { getWorkItemEditUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { convertWorkItemIdentityRefFromFieldValue } from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { IQueryDisplayColumn, IQueryResultItem, IQueryResultItemData } from "Wit/QueriesHub/Common/Interfaces";
import * as Resources from "Wit/QueriesHub/Resources";
import { WorkItemStateProvider, WorkItemTrackingDataProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { IdentityDisplayMode, IdentityView } from "Wit/UI/IdentityView";
import { TagsList } from "Wit/UI/TagsList";
import { WorkItemState } from "Wit/UI/WorkItemState";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem, IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { QueryUtils } from "../Common/Utils/QueryUtils";
import { ResultsMoreButton } from "./ResultsMoreButton";

interface IResultsTreeProps extends Pick<ITreeProps<IQueryResultItem>, "onFocus"> {
    rootRef: React.RefObject<HTMLDivElement>;
    columns?: IObservableArray<IQueryDisplayColumn>;
    isImport?: boolean;
    isShowMoreItemsSet?: ObservableValue<boolean>;
    items: IReadyableObservableArray<IQueryResultItem>;
    getItemsMap: () => Map<number, IQueryResultItem>;
    onChangeRecycleBin?: () => void;
    onDeleteItems: (items: INamedFieldDataDictionary[]) => void;
    onModifyItems?: (items: IWorkItem[]) => void;
    queryType: ObservableValue<QueryType>;
    selection: ListSelection;
    sourceIds: ObservableArray<number>;
    targetIds: ObservableArray<number>;
    titleLinkOn: boolean;
    treeRef: React.RefObject<Tree<IQueryResultItem>>;
    workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    useNewResultsTreeDesign?: boolean;
}

const ColumnMoreId = "_more";
const ExpandCollapseColumnId = "expand-collapse-column";

export class ResultsTree extends VssComponent<IResultsTreeProps> {
    private identities = new ObservableArray<IIdentity>([]);
    private queryService: IQueryService;
    private columns: ITableColumn<{}>[] = [];
    private isWorkItemTypeInColumns: boolean;
    private treeProvider = new ObservableValue<TreeItemProvider<IQueryResultItem | undefined>>(
        new TreeItemProvider<IQueryResultItem | undefined>([])
    );
    private columnReorderBehavior: ColumnDragDropBehavior;
    private contextMenuBehavior: ContextMenuBehavior;
    private moreButtonRefs: React.RefObject<ResultsMoreButton>[] = [];
    private projectId: string;
    private lastSelectedIndex = 0;
    private workItemModelService: IWorkItemModelService;
    private parentMap: { [key: number]: ObservableValue<IQueryResultItemData | undefined> } = {};
    private isExpanded = true;
    private fetchParentError = new ObservableValue<string | undefined>(undefined);
    private responsiveLayoutFlickeringFixEnabled: boolean;
    private workItemPermissions = new ObservableValue<IWorkItemPermissionData | undefined>(undefined);

    public constructor(props: IResultsTreeProps, context: IVssComponentContext) {
        super(props, context);
        this.queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        this.workItemModelService = context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.contextMenuBehavior = new ContextMenuBehavior(this.onContextMenu);
        this.projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? "";
        this.responsiveLayoutFlickeringFixEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.ResponsiveLayoutFlickeringFixEnabled, false);
        this.getIdentities();
        this.updateColumns();
        this.updateTree();
    }

    public async componentDidMount() {
        if (this.context.pageContext) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemPermissions.value = await contributionService.getDataAsync<IWorkItemPermissionData>(
                QueryDataProviders.WorkItemPermissionsDataProvider
            );
        }
    }

    public render(): JSX.Element {
        const { onFocus, selection, sourceIds, targetIds, treeRef, items } = this.props;

        return (
            <Card
                className={(this.props.useNewResultsTreeDesign ? "query-results-card-ff" : "query-results-card") + " inline-flex-column flex-grow bolt-table-card full-height full-width"}
                contentProps={{ contentPadding: false }}
            >
                <div className="flex-column flex-grow scroll-hidden" onKeyDown={this.onKeyDown}>
                    <Observer
                        queryResultItems={{ observableValue: items, filter: this.onItemsUpdated } as IObservableExpression<IQueryResultItem[]>}
                        queryResultItemsReady={{ observableValue: items.ready, filter: this.onItemsUpdated }}
                        sourceIds={{ observableValue: sourceIds, filter: this.onItemsUpdated }}
                        targetIds={{ observableValue: targetIds, filter: this.onItemsUpdated }}
                        ready={items.ready}
                        hasQueryRun={this.queryService.hasQueryRun}
                        queryResult={{ observableValue: this.queryService.queryResult, filter: this.updateColumns }}
                        columns={{ observableValue: this.props.columns, filter: this.updateColumns }}
                        treeProvider={this.treeProvider}
                        queryType={{ observableValue: this.props.queryType, filter: this.onItemsUpdated }}
                    >
                        {(observerProps: {
                            queryResultItems: IQueryResultItem[];
                            ready: boolean;
                            hasQueryRun: boolean;
                            treeProvider: TreeItemProvider<IQueryResultItem | undefined>;
                        }) => {
                            return !observerProps.ready ? (
                                <Spinner className="query-results-spinner margin-bottom-16" label={Resources.LoadingResults} />
                            ) : observerProps.queryResultItems.length === 0 ? (
                                <ZeroData
                                    className="margin-bottom-16"
                                    primaryText={!observerProps.hasQueryRun ? Resources.RunToSeeResults : Resources.NoResultsText}
                                    imagePath={getAssetLocation(
                                        this.context.pageContext,
                                        "ms.vss-work-web/new-queries-hub-content/Illustrations/general-no-results-found.svg"
                                    )}
                                    imageAltText={!observerProps.hasQueryRun ? Resources.RunToSeeResults : Resources.NoResultsText}
                                />
                            ) : (
                                <Intersection rootElement={this.props.rootRef.current!}>
                                    <>
                                        <Tree
                                            ariaLabel={Resources.QueryResultsTableLabel}
                                            ref={treeRef}
                                            className="results-tree sticky-table"
                                            containerClassName="sticky-table full-height display-above-the-shim"
                                            behaviors={[this.contextMenuBehavior, this.sortingBehavior as any, this.columnReorderBehavior]} //cast sortingBehavior to any because the tree and table types don't line up
                                            columns={this.columns}
                                            onActivate={this.onActivate}
                                            onSelect={this.onRowSelect}
                                            singleClickActivation={false}
                                            itemProvider={observerProps.treeProvider}
                                            renderRow={this.renderResultsTreeRow}
                                            scrollable={true}
                                            selection={selection}
                                            showLines={false}
                                            showScroll={true}
                                            onFocus={onFocus}
                                            onToggle={this.onToggle}
                                        />
                                    </>
                                </Intersection>
                            );
                        }}
                    </Observer>
                </div>
            </Card>
        );
    }

    private onRowSelect = (
        event: React.SyntheticEvent<HTMLElement, Event>,
        treeRow: ITreeRow<IObservableValue<IQueryResultItemData> | undefined>
    ) => {
        this.lastSelectedIndex = treeRow.index;
    };

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<IQueryResultItem>) => {
        this.queryService.changeView(QueryHubView.WorkItemEdit, treeRow.data.underlyingItem.data.value[CoreFieldRefNames.Id]);
    };

    private sortingBehavior = new ColumnSorting<ITreeItemEx<IQueryResultItem>>(
        async (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            const fieldRefName = this.columns[columnIndex].id;
            const descending = proposedSortOrder === SortOrder.descending;
            this.queryService.querySortColumns = [
                {
                    name: fieldRefName,
                    descending: descending
                }
            ];
            this.queryService.isEditorDirty.value = true;
            const wiql = await this.queryService.generateWiql(this.projectId);
            this.queryService.executeQuery(this.projectId, wiql, this.props.isShowMoreItemsSet?.value);
        }
    );

    private updateExpandCollapseColumn = () => {
        if (this.props.queryType.value !== QueryType.Flat && this.columns.length !== 0 && this.columns[0].id !== ExpandCollapseColumnId) {
            this.columns.splice(0, 0, this.expandCollapseColumn);
        }
    };

    private onItemsUpdated = () => {
        this.updateTree();
        this.updateExpandCollapseColumn();
        return true;
    };

    private onContextMenu = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>) => {
        const rowIndex = cellFromEvent(event).rowIndex;
        const mergeSelection = this.props.selection.selected(rowIndex) || !!event.shiftKey;
        this.props.selection.select(rowIndex, 1, mergeSelection);
        if (this.moreButtonRefs[rowIndex]?.current) {
            this.moreButtonRefs[rowIndex]!.current!.expand();
            event.preventDefault();
        }
    };

    private updateColumns = () => {
        this.columns = this.getColumns();
        this.isWorkItemTypeInColumns = this.columns.some(c => c.id === CoreFieldRefNames.WorkItemType);
        this.updateExpandCollapseColumn();
        const columnReorderBehaviorOptions: ColumnDragDropBehaviorOptions = {
            type: "column",
            allowedTypes: ["column"],
            columns: this.columns,
            renderDragImage: event => {
                const columnName = event.detail.dataTransfer.data;
                return <ListDragImage text={columnName.name ?? ""} />;
            },
            onDrop: this.onColumnDrop
        };

        if (this.columnReorderBehavior) {
            this.columnReorderBehavior.updateBehaviorOptions(columnReorderBehaviorOptions);
        } else {
            this.columnReorderBehavior = new ColumnDragDropBehavior(columnReorderBehaviorOptions);
        }

        return true;
    };

    private getColumns = () => {
        if (this.queryService.queryResult.value?.data?.queryRan || this.props.columns?.value.length) {
            const columnArray = this.props.columns?.value ?? this.queryService.queryResult.value!.data!.columns;
            const columns: ITreeColumn<IQueryResultItem | undefined>[] = columnArray.map((column: IQueryDisplayColumn, index: number) => {
                const isSortable = this.queryService.sortableFields.value.some(field => field.id === column.fieldId);
                const sortColumn = this.queryService.queryResult.value?.data?.sortColumns.find(sortColumn => sortColumn.name === column.name);
                const existingColumn = this.columns.find(existingColumn => existingColumn.id === column.name);
                return {
                    headerClassName: css(
                        "bolt-table-header-cell-actionable",
                        column.name === CoreFieldRefNames.Title &&
                            this.queryService.getQueryType() === QueryType.Tree &&
                            "results-tree-title-header-cell"
                    ),
                    id: column.name || index.toString(),
                    name: column.text,
                    readonly: true,
                    renderCell: this.getCellRenderer(column),
                    width: existingColumn ? existingColumn.width : new ObservableValue(column.width + QueryUtils.getColumnHeaderPadding()),
                    onSize: this.onSizeColumn,
                    sortProps: isSortable
                        ? {
                              ariaLabelAscending: Resources.SortAscendingAriaLabel,
                              ariaLabelDescending: Resources.SortDescendingAriaLabel,
                              sortOrder: sortColumn ? (sortColumn.descending ? SortOrder.descending : SortOrder.ascending) : undefined
                          }
                        : undefined
                };
            });
            this.addContextMenuColumn(columns);
            columns.push(ColumnFill);
            return columns;
        }

        return this.columns;
    };

    private onColumnDrop = async (event: BoltListDragEvent<HTMLElement, ITableColumn<{}>>, dropData: IListDropData) => {
        const movedColumn = event.detail.dataTransfer.data;
        // Add new
        const index = dropData.index;
        this.columns.splice(index, 0, movedColumn);
        // Remove old
        const movedColCurrentIndex = this.columns.findIndex((col, idx) => col.id === movedColumn.id && idx !== index);
        this.columns.splice(movedColCurrentIndex, 1);

        // Find context menu and if it's not at the right index, move it there
        const contextMenuIndex = this.columns.length > 3 ? 3 : this.columns.length;
        const existingContextMenuIdx = this.columns.findIndex(col => col.id === ColumnMoreId);
        if (existingContextMenuIdx !== contextMenuIndex) {
            this.columns.splice(existingContextMenuIdx, 1);
            this.addContextMenuColumn(this.columns);
        }
        this.queryService.queryColumns.value = this.columns
            .filter(col => col.id !== ColumnFillId && col.id !== ColumnMoreId && col.id !== ExpandCollapseColumnId)
            .map(col => col.id);
        this.queryService.isEditorDirty.value = true;
        this.props.items.ready.value = true;
    };

    private addContextMenuColumn = (columns: ITreeColumn<IQueryResultItem | undefined>[]) => {
        const contextMenuColumn = {
            ariaLabel: Resources.ContextMenuColumn,
            id: ColumnMoreId,
            readonly: true,
            renderCell: this.contextMenuCellRenderer,
            width: 32 + QueryUtils.getColumnHeaderPadding()
        };
        const contextMenuIndex = columns.length > 3 ? 3 : columns.length;
        columns.splice(contextMenuIndex, 0, contextMenuColumn);
    };

    private getCellRenderer = (column: IQueryDisplayColumn) => {
        switch (column.name) {
            case CoreFieldRefNames.Id:
                return this.idCellRenderer;
            case CoreFieldRefNames.Title:
                return this.titleCellRenderer;
            case CoreFieldRefNames.Parent:
                return this.parentCellRenderer;
            case CoreFieldRefNames.State:
                return this.stateCellRenderer;
            case CoreFieldRefNames.Tags:
                return this.tagCellRenderer;
        }

        if (column.isIdentity) {
            return this.identityCellRenderer;
        }

        const fieldType = this.queryService.allFields.value.find(field => field.referenceName === column.name)?.type;
        if (fieldType === FieldType.Html || fieldType === FieldType.History) {
            return this.htmlRenderer;
        }

        return this.genericRenderer;
    };

    private getIdentities = () => {
        const peopleProvider = new PeoplePickerProvider(this.context.pageContext, undefined, this.filterIdentities);
        const identities = peopleProvider.onEmptyInputFocus();
        if (Array.isArray(identities)) {
            this.identities.value = identities;
        } else if (identities instanceof Promise) {
            const identitiesPromise = identities as Promise<IIdentity[]>;
            identitiesPromise.then((resolvedIdentities: IIdentity[]) => {
                this.identities.value = resolvedIdentities;
            });
        }
    };

    private filterIdentities = (entityList: IIdentity[]) => entityList && entityList.filter(entity => !!entity.localId);

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        const getSelectedItems = () => {
            return QueryUtils.getSelectedItemsFromTreeItemProvider(this.props.selection, this.treeProvider.value).map(v => v.value);
        };

        if (event.key === "Delete") {
            const selectedItems = getSelectedItems();
            const hasTestItem = selectedItems.find(item => WorkItemUtils.TestItemTypes.includes(item[CoreFieldRefNames.WorkItemType]));

            const canDeleteWorkItems = this.workItemPermissions.value?.workItemDeletePermission?.hasPermission;

            const hideDelete =
                !canDeleteWorkItems || this.props.isImport || selectedItems.some(item => item[CoreFieldRefNames.Id] < 0) || hasTestItem;

            if (!hideDelete) {
                this.getDeleteDialog(selectedItems);
                event.preventDefault();
            }
        }

        if (event.ctrlKey && event.key === "c") {
            WorkItemUtils.onCopyHTML(this.context.pageContext, getSelectedItems, this.queryService.queryResult.value!.data!.columns);
            event.preventDefault();
        }
    };

    private openDialog = (callout: (onDismiss: () => void) => JSX.Element): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    };

    private getDeleteDialog(selectedItems: INamedFieldDataDictionary[]): void {
        return this.openDialog(onDismiss => (
            <DeleteDialog onDismiss={onDismiss} onDeleteItems={this.props.onDeleteItems} selectedItems={selectedItems} />
        ));
    }

    private onSizeColumn = (event: MouseEvent | KeyboardEvent, index: number, width: number) => {
        (this.columns[index].width as ObservableValue<number>).value = width;
    };

    private recursiveToggle = (item: ITreeItem<IQueryResultItem | undefined>, expand: boolean) => {
        if (item.expanded !== undefined && item.expanded !== expand) {
            this.treeProvider.value.toggle(item);
        }
        if (item.childItems === undefined || item.childItems.length === 0) {
            return;
        }
        item.childItems.forEach(child => {
            this.recursiveToggle(child, expand);
        });
    };

    private onClickExpandCollapseAll = (expand: boolean) => {
        const roots = this.treeProvider.value.roots;
        this.isExpanded = expand;
        roots.forEach(root => {
            if (root.expanded !== undefined && root.expanded !== expand) {
                this.treeProvider.value.toggle(root);
            }
            if (root.childItems && root.childItems.length !== 0) {
                root.childItems.forEach(child => {
                    this.recursiveToggle(child, expand);
                });
            }
        });
    };

    private renderHeaderCell = (columnIndex: number, tableColumn: ITreeColumn<IQueryResultItem>): JSX.Element => {
        return (
            <TableHeaderCell key={"table-header" + columnIndex} column={tableColumn} columnIndex={columnIndex}>
                <div className="flex-row flex-start">
                    <div className="padding-2">
                        <Button
                            ariaLabel={Resources.ExpandAll}
                            className="expand-collapse-button"
                            iconProps={{ iconName: "Add", tooltipProps: { text: Resources.ExpandAll }, size: IconSize.small }}
                            onClick={() => this.onClickExpandCollapseAll(true)}
                        />
                    </div>
                    <div className="padding-2">
                        <Button
                            ariaLabel={Resources.CollapseAll}
                            className="expand-collapse-button"
                            iconProps={{ iconName: "Remove", tooltipProps: { text: Resources.CollapseAll }, size: IconSize.small }}
                            onClick={() => this.onClickExpandCollapseAll(false)}
                        />
                    </div>
                </div>
            </TableHeaderCell>
        );
    };

    private expandCollapseColumn: ITreeColumn<IQueryResultItem> = {
        id: ExpandCollapseColumnId,
        renderHeaderCell: this.renderHeaderCell,
        renderCell: renderEmptyCell,
        width: new ObservableValue(70),
        onSize: this.onSizeColumn
    };

    private renderResultsTreeRow = (
        rowIndex: number,
        item: ITreeItemEx<IQueryResultItem | undefined>,
        details: ITreeRowDetails<IQueryResultItem | undefined>
    ) => {
        const isNewWorkItem = item.underlyingItem.data?.value[CoreFieldRefNames.Id] < 0;
        const isMissingData = item.underlyingItem.data?.value[CoreFieldRefNames.State] === undefined; // State should always be present

        if (isMissingData && !isNewWorkItem) {
            return <LoadingRow columns={this.columns} details={details} pageContext={this.context.pageContext} rowIndex={rowIndex} treeItem={item} />;
        }

        return (
            <Observer queryResultItem={item?.underlyingItem.data}>
                {({ queryResultItem }: { queryResultItem: IQueryResultItemData }) => {
                    return renderTreeRow(
                        rowIndex,
                        item,
                        details,
                        this.columns,
                        item.underlyingItem.data,
                        css(
                            (queryResultItem.isDirty || queryResultItem.isInvalid) && "font-weight-heavy",
                            queryResultItem.isInvalid && "error-text-important"
                        )
                    );
                }}
            </Observer>
        );
    };

    private idCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const isInvalid = workItem.underlyingItem.data.value.isInvalid;
        return (
            <SimpleTableCell columnIndex={columnindex} key={"id" + rowindex}>
                {isInvalid && <Icon className={"icon-large-margin error-text-important font-weight-semibold"} iconName="AlertSolid" />}
                {workItem.underlyingItem.data.value[CoreFieldRefNames.Id] > 0 && (
                    <span className="text-ellipsis">{workItem.underlyingItem.data.value[CoreFieldRefNames.Id]}</span>
                )}
            </SimpleTableCell>
        );
    };

    private workItemLinkRenderer = (rowindex: number, columnindex: number, workItem: IQueryResultItemData, singleClickLink?: boolean) => {
        const teamProject = workItem[CoreFieldRefNames.TeamProject];
        const title = workItem[CoreFieldRefNames.Title];
        const workItemType = workItem[CoreFieldRefNames.WorkItemType];
        const id = workItem[CoreFieldRefNames.Id];
        const linkOn = singleClickLink || this.props.titleLinkOn;
        const selection = this.props.selection;

        // Only show a link for saved work items
        const titleElement =
            id < 0 ? (
                title
            ) : (
                <Link
                    className="text-ellipsis"
                    href={getWorkItemEditUrl(this.context.pageContext, id, teamProject).url}
                    tooltipProps={{ overflowOnly: true }}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.KeyboardEvent<HTMLAnchorElement>) => {
                        if (!event.ctrlKey) {
                            event.preventDefault();
                            if (linkOn) {
                                this.queryService.changeView(QueryHubView.WorkItemEdit, id);
                            } else {
                                selection.select(rowindex);
                            }
                        }
                    }}
                    removeUnderline={true}
                >
                    {title}
                </Link>
            );

        return (
            <div className="flex-row flex-grow scroll-hidden">
                <WorkItemTypeIconProvider
                    dataProvider={this.props.workItemTrackingDataProvider}
                    dataSource={{
                        // Fallback to the projectId if we are displaying an imported work item that is new
                        // (and doesn't have a team project yet)
                        projectIdOrName: teamProject ?? this.projectId,
                        workItemTypeName: workItemType
                    }}
                >
                    {icon => <WorkItemTypeIcon suppressAriaLabel={this.isWorkItemTypeInColumns} data={icon} className="icon-margin font-size-m" />}
                </WorkItemTypeIconProvider>
                {titleElement}
            </div>
        );
    };

    private titleCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        if (!this.moreButtonRefs[rowindex]) {
            this.moreButtonRefs[rowindex] = React.createRef<ResultsMoreButton>();
        }

        const isFlat = this.props.queryType.value === QueryType.Flat;

        return (
            <ExpandableTreeCell className={css(isFlat && "flat-query")} columnIndex={columnindex} key={"title" + rowindex} treeItem={workItem}>
                {this.workItemLinkRenderer(rowindex, columnindex, workItem.underlyingItem.data.value)}
            </ExpandableTreeCell>
        );
    };

    private parentCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const parentId = workItem.underlyingItem.data.value[CoreFieldRefNames.Parent];
        let parentItem: IQueryResultItem | undefined;
        if (parentId !== undefined && parentId !== null) {
            if (!this.parentMap[parentId]) {
                this.parentMap[parentId] = new ObservableValue<IQueryResultItemData | undefined>(undefined);
                // see if we've already loaded the parent item, otherwise fetch it.
                const itemsMap = this.props.getItemsMap();
                parentItem = itemsMap.get(parentId);
                if (parentItem) {
                    this.parentMap[parentId].value = parentItem.value;
                } else {
                    const parent = this.queryService.getParentMap().get(parentId);
                    if (parent) {
                        this.parentMap[parentId].value = parent;
                    } else {
                        this.fetchParent(parentId);
                    }
                }
            }
        }

        return (
            <SimpleTableCell columnIndex={columnindex} key={"parent" + rowindex}>
                <Observer parentItem={this.parentMap[parentId]} fetchParentError={this.fetchParentError}>
                    {(observableProps: { parentItem: IQueryResultItemData; fetchParentError: string | undefined }) => {
                        if (this.fetchParentError.value) {
                            return (
                                <Icon
                                    className={"icon-large-margin error-text-important font-weight-semibold"}
                                    iconName="AlertSolid"
                                    tooltipProps={{ text: this.fetchParentError.value }}
                                />
                            );
                        }
                        return observableProps.parentItem ? this.workItemLinkRenderer(rowindex, columnindex, observableProps.parentItem, true) : null;
                    }}
                </Observer>
            </SimpleTableCell>
        );
    };

    private fetchParent = async (parentId: number) => {
        try {
            const parentModel = await this.workItemModelService.getWorkItem(parentId);
            const parentItem: IQueryResultItemData = {};
            parentItem[CoreFieldRefNames.Id] = parentModel.getFieldValueByName(CoreFieldRefNames.Id);
            parentItem[CoreFieldRefNames.Title] = parentModel.getFieldValueByName(CoreFieldRefNames.Title);
            parentItem[CoreFieldRefNames.TeamProject] = parentModel.getFieldValueByName(CoreFieldRefNames.TeamProject);
            parentItem[CoreFieldRefNames.WorkItemType] = parentModel.getFieldValueByName(CoreFieldRefNames.WorkItemType);
            this.parentMap[parentId].value = parentItem;
        } catch (error) {
            this.fetchParentError.value = error.message;
        }
    };

    private contextMenuCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => (
        <SimpleTableCell contentClassName="bolt-list-cell-content-reveal" columnIndex={columnindex} key={"contextMenu" + rowindex}>
            <ResultsMoreButton
                isImport={this.props.isImport}
                onChangeRecycleBin={this.props.onChangeRecycleBin}
                onClick={(event: React.MouseEvent | React.KeyboardEvent) => {
                    if (event.shiftKey) {
                        this.props.selection.select(
                            Math.min(this.lastSelectedIndex, rowindex),
                            Math.abs(this.lastSelectedIndex - rowindex) + 1,
                            true
                        );
                    } else if (!this.props.selection.selected(rowindex)) {
                        // Only update the selection if the current row isn't already a part of it
                        // This allows the user to select multiple rows with the shift key,
                        // then release the shift key and click the ResultsMore button on a selected row
                        const mergeSelection = this.props.selection.selected(rowindex) || event.ctrlKey;
                        this.props.selection.select(rowindex, 1, mergeSelection);
                    }
                    this.lastSelectedIndex = rowindex;
                }}
                onDeleteItems={this.props.onDeleteItems}
                onModifyItems={this.onModifyItems}
                ref={this.moreButtonRefs[rowindex]}
                identities={this.identities}
                selection={this.props.selection}
                treeItemProvider={this.treeProvider}
                itemsReady={this.props.items.ready}
                getWorkItems={idsToUpdate => this.getWorkItems(idsToUpdate)}
            />
        </SimpleTableCell>
    );

    private identityCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const fieldName = treeColumn.id;
        const identity = workItem.underlyingItem.data.value[fieldName];
        const identityRef = identity && convertWorkItemIdentityRefFromFieldValue(this.context.pageContext, identity).identityRef;
        return (
            <SimpleTableCell columnIndex={columnindex} key={fieldName + rowindex + columnindex}>
                {identityRef && (
                    <IdentityView
                        identityRef={identityRef}
                        displayTooltipOnHover={true}
                        className="text-ellipsis"
                        viewMode={IdentityDisplayMode.NameOnly}
                    />
                )}
            </SimpleTableCell>
        );
    };

    private stateCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const state = workItem.underlyingItem.data.value[CoreFieldRefNames.State];
        const teamProject = workItem.underlyingItem.data.value[CoreFieldRefNames.TeamProject] || this.projectId;
        const workItemType = workItem.underlyingItem.data.value[CoreFieldRefNames.WorkItemType];
        return (
            <SimpleTableCell columnIndex={columnindex} key={"state" + rowindex}>
                <WorkItemStateProvider
                    dataProvider={this.props.workItemTrackingDataProvider}
                    dataSource={{
                        projectIdOrName: teamProject,
                        workItemTypeName: workItemType,
                        stateName: state
                    }}
                >
                    {stateAndColor => <WorkItemState className={"my-work-list-column"} data={stateAndColor} hideText={false} />}
                </WorkItemStateProvider>
            </SimpleTableCell>
        );
    };

    private tagCellRenderer = (
        rowindex: number,
        columnindex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        workItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const workItemTags = workItem.underlyingItem.data.value[CoreFieldRefNames.Tags];
        const tags = workItemTags ? workItemTags.split(";") : [];

        // observe width so responsive layout gets updated, and consequently displayed tags are recalculated
        return (
            <Observer width={treeColumn.width} key={"tags-cell-" + rowindex}>
                {() => (
                    <SimpleTableCell columnIndex={columnindex}>
                        <TagsList responsiveLayoutFlickeringFixEnabled={this.responsiveLayoutFlickeringFixEnabled} tags={tags} />
                    </SimpleTableCell>
                )}
            </Observer>
        );
    };

    private htmlRenderer = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        treeItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const fieldName = treeColumn.id;
        const htmlText = treeItem.underlyingItem.data.value[fieldName];
        const cellContent = convertValueToDisplayString(extractText(htmlText));
        return (
            <SimpleTableCell
                className={treeColumn.className}
                columnIndex={columnIndex}
                key={`results-${treeColumn.id}-${rowIndex}`}
                tableColumn={treeColumn}
            >
                {cellContent && renderListCell(cellContent)}
            </SimpleTableCell>
        );
    };

    private genericRenderer = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IQueryResultItem>,
        treeItem: ITreeItemEx<IQueryResultItem>
    ) => {
        const fieldName = treeColumn.id;
        const value = treeItem.underlyingItem.data.value[fieldName];
        const cellContent = convertValueToDisplayString(value);
        return (
            <SimpleTableCell
                className={treeColumn.className}
                columnIndex={columnIndex}
                key={`results-${treeColumn.id}-${rowIndex}`}
                tableColumn={treeColumn}
            >
                {cellContent && renderListCell(cellContent)}
            </SimpleTableCell>
        );
    };

    private onModifyItems = (items: IWorkItem[]): void => {
        // add any work items that aren't in the list already
        const ownedIds = new Set<number>(this.queryService.workItems.value.map(item => item.id));
        this.queryService.workItems.push(...items.filter(item => !ownedIds.has(item.id)));
        this.props.onModifyItems?.(items);
    };

    private getWorkItems = (idsToUpdate: number[]): IWorkItem[] | undefined => {
        if (!this.props.isImport) {
            return undefined;
        }

        const result: IWorkItem[] = [];

        for (const id of idsToUpdate) {
            const isNewWorkItem = id < 0;
            const workItem = this.queryService.workItems.value.find(wi => id === (isNewWorkItem ? wi.tempId : wi.id));
            if (!workItem) {
                continue;
            }
            result.push(workItem);
        }

        return result;
    };

    private updateTree = (): void => {
        const { items } = this.props;
        const queryType = this.props.queryType.value;
        const sourceIds = this.props.sourceIds.value;
        const targetIds = this.props.targetIds.value;
        const itemsMap = this.props.getItemsMap();

        if (!items.ready.value) {
            return;
        }

        const expandedItems = this.getExpandedItems(this.treeProvider.value.roots);
        const treeItems: ITreeItem<IQueryResultItem | undefined>[] = [];
        if (queryType === QueryType.Tree) {
            // use object references to construct the tree
            const objectDict: Dictionary<ITreeItem<IQueryResultItem>> = {};

            targetIds.forEach(id => {
                if (!objectDict.hasOwnProperty(id)) {
                    const queryResultItem = itemsMap.get(id);
                    if (queryResultItem) {
                        objectDict[id] = { data: queryResultItem };
                    }
                }
            });
            for (let i = 0; i < targetIds.length; i++) {
                if (!objectDict[targetIds[i]]) {
                    continue;
                }
                if (sourceIds[i] === 0) {
                    treeItems.push(objectDict[targetIds[i]]);
                } else {
                    if (!objectDict[sourceIds[i]]) {
                        continue;
                    }
                    if (objectDict[sourceIds[i]].childItems) {
                        objectDict[sourceIds[i]].childItems?.push(objectDict[targetIds[i]]);
                    } else {
                        objectDict[sourceIds[i]].childItems = [objectDict[targetIds[i]]];
                        objectDict[sourceIds[i]].expanded = expandedItems[sourceIds[i]] ?? this.isExpanded;
                    }
                }
            }
        } else if (queryType === QueryType.OneHop) {
            // using object references can create circular refs for links (due to forward and reverse links)
            let lastParent: ITreeItem<IQueryResultItem | undefined> = { data: undefined };
            const childItems = new Set<number>();
            for (let i = 0; i < targetIds.length; i++) {
                const workItemId = targetIds[i];

                const item = itemsMap.get(workItemId);

                if (!item) {
                    continue;
                }

                if (sourceIds[i] === 0) {
                    lastParent = { data: item };
                    treeItems.push(lastParent);
                    childItems.clear();
                } else if (lastParent && !childItems.has(workItemId)) {
                    if (lastParent.childItems) {
                        lastParent.childItems.push({ data: item });
                    } else {
                        lastParent.childItems = [{ data: item }];
                        lastParent.expanded = expandedItems[sourceIds[i]] ?? this.isExpanded;
                    }
                }

                childItems.add(workItemId);
            }
        } else {
            //one hop queries can have duplicating workItem ids inside targetIds
            const parentItems = new Set<number>();
            targetIds.forEach(id => {
                const item = itemsMap.get(id);
                if (item && !parentItems.has(id)) {
                    treeItems.push({ data: item });
                    parentItems.add(id);
                }
            });
        }

        this.treeProvider.value = new TreeItemProvider<IQueryResultItem | undefined>(treeItems);
    };

    private getExpandedItems = (items: ITreeItem<IQueryResultItem | undefined>[]): { [id: number]: boolean } => {
        let expandedItems: { [id: number]: boolean } = {};
        items.forEach(item => {
            if (item.data?.value[CoreFieldRefNames.Id] && item.expanded !== undefined) {
                expandedItems[item.data?.value[CoreFieldRefNames.Id]] = item.expanded;
                if (item.childItems && item.childItems.length > 0) {
                    expandedItems = { ...expandedItems, ...this.getExpandedItems(item.childItems) };
                }
            }
        });

        return expandedItems;
    };

    private onToggle = (event: React.SyntheticEvent<HTMLElement>, treeItem: ITreeItemEx<IQueryResultItem>) => {
        if (
            (event.target as HTMLSpanElement).className.includes("bolt-tree-expand-button") &&
            treeItem.underlyingItem.childItems &&
            treeItem.underlyingItem.childItems.length !== 0
        ) {
            if (!this.queryService.isWorkItemAlreadyFetched(treeItem.underlyingItem.data)) {
                this.queryService.fetchWorkItem(treeItem.underlyingItem.data.value[CoreFieldRefNames.Id]);
            }
            this.treeProvider.value.toggle(treeItem.underlyingItem);

            const rowIndex = cellFromEvent(event).rowIndex;
            if (rowIndex !== -1) {
                this.props.selection.select(rowIndex);
            }
        }
    }
}

function extractText(htmlText: string): string {
    if (!htmlText) {
        return "";
    }
    const tmp = document.implementation.createHTMLDocument("").createElement("div");
    tmp.innerHTML = htmlText;
    return tmp.innerText || tmp.textContent || "";
}

interface ILoadingRowProps {
    columns: ITreeColumn<IQueryResultItem>[];
    details: ITreeRowDetails<IQueryResultItem | undefined>;
    pageContext: IVssPageContext;
    rowIndex: number;
    treeItem: ITreeItemEx<IQueryResultItem | undefined>;
}

function LoadingRow(props: ILoadingRowProps): JSX.Element {
    const { columns, details, pageContext, rowIndex, treeItem } = props;

    // attempt to load the data for this row on first render
    // it's possible to have an unloaded root item, which is why we do this here
    // even though we also fetch any unloaded children when a row is expanded
    // it's not a problem to attempt a double-fetch those child rows
    React.useEffect(() => {
        const queryService = pageContext.getService<IQueryService>("IQueryService");
        queryService.fetchWorkItem(treeItem.underlyingItem.data?.value[CoreFieldRefNames.Id]);
    }, []);

    return (
        <TreeRow index={rowIndex} details={details}>
            {columns.map((treeColumn: ITreeColumn<IQueryResultItem>, columnIndex: number) => {
                // render ID cell in loading row
                if (equals(treeColumn.id, CoreFieldRefNames.Id)) {
                    return (
                        <SimpleTableCell columnIndex={columnIndex} key={"id" + rowIndex}>
                            {treeItem.underlyingItem.data?.value[CoreFieldRefNames.Id] > 0 && (
                                <span className="text-ellipsis">{treeItem.underlyingItem.data?.value[CoreFieldRefNames.Id]}</span>
                            )}
                        </SimpleTableCell>
                    );
                }
                const children = renderLoadingCell(treeColumn.columnLayout);
                return SimpleTableCell({ className: "bolt-tree-cell", columnIndex, children });
            })}
        </TreeRow>
    );
}
